import { cs } from '../../lib/currency';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { useTenant } from '../../context/TenantContext';
import api from '../../services/api';
import { DashboardLayout } from '../../components/DashboardLayout';
import {
  BookOpen,
  Clock,
  Users,
  CheckCircle,
  ArrowLeft,
  Loader2,
  AlertCircle,
} from 'lucide-react';
import { useStructuredData, getCourseSchema } from '../../components/StructuredData';

const PAYMENT_FREQUENCIES = [
  { value: 'monthly', label: 'Monthly', multiplier: 1 },
  { value: 'quarterly', label: 'Quarterly (3 months)', multiplier: 3 },
  { value: 'biannual', label: 'Bi-Annual (6 months)', multiplier: 6 },
];

const PlayerPrograms = () => {
  const navigate = useNavigate();
  const { _user } = useAuth();
  const { branding } = useTenant();
  const tenantName = branding?.business_name || 'Cricket Academy';
  const [loading, setLoading] = useState(true);
  const [programs, setPrograms] = useState([]);
  const [enrolling, setEnrolling] = useState(false);
  
  // Enrollment dialog state
  const [showEnrollDialog, setShowEnrollDialog] = useState(false);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedFrequency, setSelectedFrequency] = useState('monthly');
  const [calculatedFee, setCalculatedFee] = useState(0);
  const [adultMessage, setAdultMessage] = useState(null);

  useEffect(() => {
    fetchPrograms();
  }, []);

  const fetchPrograms = async () => {
    try {
      const response = await api.get('/player/programs');
      setPrograms(response.data.programs || []);
      setAdultMessage(response.data.adult_message || null);
    } catch (error) {
      toast.error('Failed to load programs');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Generate ItemList schema for all programs (for SEO)
  const programListSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Cricket Training Programs",
    "description": `Professional cricket coaching programs for all skill levels at ${tenantName}`,
    "numberOfItems": programs.length,
    "itemListElement": programs.map((program, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": getCourseSchema(program, tenantName)
    }))
  };

  // Inject structured data into page
  useStructuredData(programs.length > 0 ? programListSchema : null);

  const openEnrollDialog = (program) => {
    setSelectedProgram(program);
    setSelectedClass('');
    setSelectedFrequency('monthly');
    setCalculatedFee(program.price || 0);
    setShowEnrollDialog(true);
  };

  const handleFrequencyChange = (frequency) => {
    setSelectedFrequency(frequency);
    if (selectedProgram) {
      let fee = selectedProgram.price || 0;
      if (frequency === 'quarterly' && selectedProgram.quarterly_price) {
        fee = selectedProgram.quarterly_price;
      } else if (frequency === 'biannual' && selectedProgram.biannual_price) {
        fee = selectedProgram.biannual_price;
      }
      setCalculatedFee(fee);
    }
  };

  const handleEnroll = async () => {
    if (!selectedClass) {
      toast.error('Please select a class');
      return;
    }

    setEnrolling(true);
    try {
      const response = await api.post('/player/enroll', {
        program_id: selectedProgram.id,
        class_id: selectedClass,
        payment_frequency: selectedFrequency
      });
      
      toast.success(response.data.message);
      setShowEnrollDialog(false);
      
      // Refresh programs to update availability
      fetchPrograms();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to enroll');
    } finally {
      setEnrolling(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="player-programs-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <Button 
              variant="ghost" 
              onClick={() => navigate('/player')}
              className="mb-2"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Dashboard
            </Button>
            <h1 className="text-xl lg:text-2xl font-bold text-slate-900">Available Programs</h1>
            <p className="text-slate-600">Browse and enroll in cricket programs</p>
          </div>
        </div>

        {/* Adult Player Message */}
        {adultMessage && (
          <Card className="bg-blue-50 border-blue-200" data-testid="adult-message-card">
            <CardContent className="py-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-blue-800 font-medium">Adult Player Programs</p>
                  <p className="text-blue-700 text-sm mt-1">{adultMessage}</p>
                  <Button 
                    variant="link" 
                    className="text-blue-600 p-0 h-auto mt-2"
                    onClick={() => navigate('/player/contact')}
                  >
                    Contact Us for Custom Coaching →
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Programs Grid */}
        {programs.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {programs.map((program) => (
              <Card 
                key={program.id} 
                className="hover:shadow-lg transition-shadow"
                data-testid={`program-card-${program.id}`}
              >
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BookOpen className="h-5 w-5 text-blue-600" />
                    {program.name}
                  </CardTitle>
                  <CardDescription>{program.description}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Program Details */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <Clock className="h-4 w-4" />
                      <span>{program.duration || 60} minutes</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <Users className="h-4 w-4" />
                      <span>Max {program.max_students || 20} students</span>
                    </div>
                  </div>

                  {/* Pricing */}
                  <div className="bg-slate-50 rounded-lg p-3 space-y-1">
                    <p className="text-sm font-medium text-slate-700">Pricing Options:</p>
                    <div className="grid grid-cols-3 gap-2 text-sm">
                      <div className="text-center">
                        <p className="font-bold text-blue-600">{cs()}{program.price}</p>
                        <p className="text-xs text-slate-500">Monthly</p>
                      </div>
                      {program.quarterly_price && (
                        <div className="text-center">
                          <p className="font-bold text-purple-600">{cs()}{program.quarterly_price}</p>
                          <p className="text-xs text-slate-500">Quarterly</p>
                        </div>
                      )}
                      {program.biannual_price && (
                        <div className="text-center">
                          <p className="font-bold text-indigo-600">{cs()}{program.biannual_price}</p>
                          <p className="text-xs text-slate-500">Bi-Annual</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Available Classes */}
                  <div>
                    <p className="text-sm font-medium text-slate-700 mb-2">
                      Available Classes ({program.classes?.length || 0})
                    </p>
                    <div className="space-y-1 max-h-24 overflow-y-auto">
                      {program.classes?.slice(0, 3).map((cls) => (
                        <div 
                          key={cls.id}
                          className="text-xs bg-white border rounded px-2 py-1 flex justify-between"
                        >
                          <span>{cls.name}</span>
                          <span className="text-slate-500">
                            {cls.day_of_week} @ {cls.start_time}
                          </span>
                        </div>
                      ))}
                      {program.classes?.length > 3 && (
                        <p className="text-xs text-slate-500">
                          +{program.classes.length - 3} more classes
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Enroll Button */}
                  <Button 
                    className="w-full bg-blue-600 hover:bg-blue-700"
                    onClick={() => openEnrollDialog(program)}
                    disabled={!program.classes?.length}
                    data-testid={`enroll-btn-${program.id}`}
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Enroll Now
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="text-center py-12">
            <CardContent>
              <BookOpen className="h-12 w-12 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">No programs available at this time</p>
            </CardContent>
          </Card>
        )}

        {/* Enrollment Dialog */}
        <Dialog open={showEnrollDialog} onOpenChange={setShowEnrollDialog}>
          <DialogContent className="sm:max-w-md" data-testid="enroll-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <BookOpen className="h-5 w-5 text-blue-600" />
                Enroll in {selectedProgram?.name}
              </DialogTitle>
              <DialogDescription>
                Select a class and payment frequency to complete your enrollment request.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              {/* Class Selection */}
              <div className="space-y-2">
                <Label>Select Class *</Label>
                <Select value={selectedClass} onValueChange={setSelectedClass}>
                  <SelectTrigger data-testid="class-select">
                    <SelectValue placeholder="Choose a class" />
                  </SelectTrigger>
                  <SelectContent>
                    {selectedProgram?.classes?.map((cls) => (
                      <SelectItem key={cls.id} value={cls.id}>
                        {cls.name} - {cls.day_of_week} @ {cls.start_time}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Payment Frequency */}
              <div className="space-y-2">
                <Label>Payment Frequency</Label>
                <Select value={selectedFrequency} onValueChange={handleFrequencyChange}>
                  <SelectTrigger data-testid="frequency-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAYMENT_FREQUENCIES.map((freq) => (
                      <SelectItem key={freq.value} value={freq.value}>
                        {freq.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Fee Summary */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <span className="text-slate-700">
                    {selectedFrequency === 'monthly' ? 'Monthly' : 
                     selectedFrequency === 'quarterly' ? 'Quarterly' : 'Bi-Annual'} Fee:
                  </span>
                  <span className="text-2xl font-bold text-blue-600">
                    {cs()}{calculatedFee?.toFixed(2)}
                  </span>
                </div>
                <p className="text-xs text-slate-500 mt-2">
                  Your enrollment request will be reviewed by the academy. 
                  Payment will be due after approval.
                </p>
              </div>

              {/* Info Notice */}
              <div className="flex items-start gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
                <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <p>
                  Enrollment requests require admin approval. You'll receive a notification 
                  once your request is processed.
                </p>
              </div>
            </div>

            <DialogFooter>
              <Button 
                variant="outline" 
                onClick={() => setShowEnrollDialog(false)}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleEnroll}
                disabled={enrolling || !selectedClass}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="confirm-enroll-btn"
              >
                {enrolling ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Submit Enrollment
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default PlayerPrograms;
